/**
 * @fileoverview Utility class for managing delayed/debounced actions.
 * @module utils/DelayedAction
 */


/**
 * Manages a delayed action with schedule, clear, and trigger capabilities.
 * Useful for debouncing searches, auto-saves, and other deferred operations.
 *
 * @class
 * @example
 * const delayedSave = new DelayedAction(() => saveData(), 500);
 * delayedSave.schedule();  // Will save after 500ms
 * delayedSave.schedule();  // Cancels previous, restarts 500ms timer
 * delayedSave.trigger();   // Execute immediately and clear timer
 */
export class DelayedAction {
    /**
     * Creates a new DelayedAction instance.
     * @param {Function} callback - Function to execute after delay
     * @param {number} [delay=500] - Delay in milliseconds
     */
    constructor(callback, delay = 500) {
        this.callback = callback;
        this.delay = delay;
        this.timerId = null;
    }


    /**
     * Schedules the callback to run after the delay.
     * Clears any existing pending timer first.
     * @param {...*} args - Arguments to pass to the callback
     */
    schedule(...args) {
        this.clear();
        this.timerId = setTimeout(() => {
            this.timerId = null;
            this.callback(...args);
        }, this.delay);
    }


    /**
     * Clears any pending scheduled action.
     */
    clear() {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    }


    /**
     * Triggers the callback immediately, clearing any pending timer.
     * @param {...*} args - Arguments to pass to the callback
     */
    trigger(...args) {
        this.clear();
        this.callback(...args);
    }


    /**
     * Checks if an action is currently scheduled.
     * @returns {boolean} True if action is pending
     */
    isPending() {
        return this.timerId !== null;
    }


    /**
     * Updates the delay for future scheduled actions.
     * Does not affect currently pending action.
     * @param {number} delay - New delay in milliseconds
     */
    setDelay(delay) {
        this.delay = delay;
    }
}
